import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Building2,
  CalendarDays,
  CheckCircle2,
  HeartHandshake,
  HelpingHand,
  MapPin,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import WhatsAppCommunityCard from "./components/WhatsAppCommunityCard";
import { trackCta } from "./ctaTracking";
import {
  businessNetworkEventPath,
  formatBusinessNetworkEventDate,
  formatBusinessNetworkEventTime,
} from "./businessNetworkEvents";
import useBusinessNetworkEvents from "./useBusinessNetworkEvents";

const primaryActions = [
  {
    to: "/events",
    icon: CalendarDays,
    title: "What’s happening locally?",
    description: "Find approved community events, classes, workshops and activities.",
    cta: "Browse local events",
    trackingId: "home-events",
  },
  {
    to: "/directory",
    icon: Search,
    title: "Looking for support?",
    description: "Search trusted local organisations, services and community groups.",
    cta: "Search the directory",
    trackingId: "home-directory",
  },
  {
    to: "/map",
    icon: MapPin,
    title: "What’s near you?",
    description: "Explore useful places and services on the interactive community map.",
    cta: "Explore the map",
    trackingId: "home-map",
  },
  {
    to: "/phoenix-hub",
    icon: Building2,
    title: "Need a community space?",
    description: "View Phoenix Hub spaces, facilities and booking information.",
    cta: "Visit Phoenix Hub",
    trackingId: "home-phoenix-hub",
  },
];

const supportPaths = [
  {
    to: "/business-network",
    icon: Briefcase,
    title: "Urban Unity Business Network",
    description: "Connect with local businesses, professionals and organisations through the monthly network.",
    trackingId: "home-business-network",
  },
  {
    to: "/food-bank",
    icon: HelpingHand,
    title: "Food support",
    description: "Find current information about UUN food support and volunteering.",
    trackingId: "home-food-bank",
  },
  {
    to: "/community-voice",
    icon: Users,
    title: "Have your say",
    description: "Share local priorities and help shape future community activity.",
    trackingId: "home-community-voice",
  },
  {
    to: "/get-involved",
    icon: HeartHandshake,
    title: "Get involved",
    description: "Volunteer, partner with UUN or suggest an improvement for the area.",
    trackingId: "home-get-involved",
  },
];

export default function Home() {
  const { nextEvent } = useBusinessNetworkEvents();
  const nextRegistrationOpen = nextEvent?.registrationOpen === true;
  const businessDestination = nextEvent && nextRegistrationOpen
    ? `${businessNetworkEventPath(nextEvent)}?source=homepage`
    : "/business-network";

  return (
    <div className="space-y-12 md:space-y-16">
      <section
        aria-labelledby="business-network-announcement-title"
        className="relative overflow-hidden rounded-3xl border border-[#ff7a84]/45 bg-gradient-to-r from-[#E63946]/30 via-fuchsia-500/15 to-cyan-400/10 p-5 shadow-xl md:grid md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-8 md:p-8"
      >
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#E63946]/25 blur-3xl" />
        <div className="relative">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-[#ff9aa2]">
            {nextEvent && nextRegistrationOpen ? "Next monthly event — registration open" : "Monthly local business programme"}
          </p>
          <h2 id="business-network-announcement-title" className="mt-2 text-3xl font-black tracking-tight md:text-4xl">
            {nextEvent?.title || "Urban Unity Business Network"}
          </h2>
          <p className="mt-3 max-w-3xl text-lg font-bold text-white">Connect locally. Grow together.</p>
          {nextEvent ? (
            <div className="mt-4 flex flex-col gap-3 text-sm text-white/80 sm:flex-row sm:flex-wrap sm:gap-x-6">
              <span className="inline-flex items-center gap-2">
                <CalendarDays className="h-5 w-5 shrink-0 text-[#ff9aa2]" aria-hidden="true" />
                {formatBusinessNetworkEventDate(nextEvent)}, {formatBusinessNetworkEventTime(nextEvent)}
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-5 w-5 shrink-0 text-[#ff9aa2]" aria-hidden="true" />
                {nextEvent.venueName || "Venue details on the event page"}
              </span>
            </div>
          ) : (
            <p className="mt-4 max-w-3xl text-sm leading-6 text-white/75">
              Meet local businesses, entrepreneurs, professionals and organisations on the last Wednesday of each month.
            </p>
          )}
        </div>
        <Link
          to={businessDestination}
          data-cta="home-business-network-announcement"
          onClick={() => trackCta("home-business-network-announcement", businessDestination)}
          className="relative mt-6 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-lg font-black text-slate-950 shadow-lg transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:mt-0 md:w-auto"
        >
          {nextEvent && nextRegistrationOpen ? "Register free" : "View the network"} <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </section>

      <section
        aria-labelledby="home-title"
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/10 via-white/5 to-[#E63946]/10 px-5 py-10 text-center shadow-2xl md:px-12 md:py-16"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#E63946]/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/20 px-4 py-2 text-sm font-semibold text-white/80">
            <MapPin className="h-4 w-4 text-[#ff7a84]" aria-hidden="true" />
            Your community hub for the Isle of Dogs
          </p>

          <h1 id="home-title" className="text-4xl font-black tracking-tight md:text-6xl">
            Find local support, events and opportunities in one place
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-white/75 md:text-xl md:leading-8">
            Urban Unity Network helps residents connect with trusted services, community activities and practical ways to get involved across the local area.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/directory"
              data-cta="home-hero-directory"
              onClick={() => trackCta("home-hero-directory", "/directory")}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#E63946] px-6 py-3 font-bold text-white shadow-lg transition hover:bg-[#d62f3d] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Find local support <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
            <Link
              to="/events"
              data-cta="home-hero-events"
              onClick={() => trackCta("home-hero-events", "/events")}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-6 py-3 font-bold text-white transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              See upcoming events
            </Link>
          </div>

          <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/75" aria-label="Why residents can trust this service">
            <li className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300" aria-hidden="true" /> Free to use</li>
            <li className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-300" aria-hidden="true" /> Community information is reviewed</li>
            <li className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-emerald-300" aria-hidden="true" /> Built around local needs</li>
          </ul>
        </div>
      </section>

      <section aria-labelledby="start-here-title">
        <div className="mb-6 max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff8b94]">Start here</p>
          <h2 id="start-here-title" className="mt-2 text-3xl font-bold md:text-4xl">What would you like to do?</h2>
          <p className="mt-3 text-white/70">Choose the route that best matches what you need today.</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {primaryActions.map(({ to, icon: Icon, title, description, cta, trackingId }) => (
            <Link
              key={to}
              to={to}
              data-cta={trackingId}
              onClick={() => trackCta(trackingId, to)}
              className="group flex min-h-64 flex-col rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:-translate-y-1 hover:border-[#E63946]/50 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#E63946]/15 text-[#ff7a84]">
                <Icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-xl font-bold">{title}</h3>
              <p className="mt-2 flex-1 text-sm leading-6 text-white/70">{description}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-semibold text-white">
                {cta} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="community-support-title" className="rounded-3xl border border-white/10 bg-black/20 p-5 md:p-8">
        <div className="mb-6 max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#ff8b94]">Community pathways</p>
          <h2 id="community-support-title" className="mt-2 text-2xl font-bold md:text-3xl">Support, participation and local action</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {supportPaths.map(({ to, icon: Icon, title, description, trackingId }) => (
            <Link
              key={to}
              to={to}
              data-cta={trackingId}
              onClick={() => trackCta(trackingId, to)}
              className="group rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <Icon className="h-7 w-7 text-[#ff7a84]" aria-hidden="true" />
              <h3 className="mt-4 text-lg font-bold">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-white/70">{description}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold">Learn more <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" /></span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="contribute-title" className="grid gap-6 rounded-3xl border border-[#E63946]/30 bg-[#E63946]/10 p-6 md:grid-cols-[1fr_auto] md:items-center md:p-8">
        <div>
          <h2 id="contribute-title" className="text-2xl font-bold md:text-3xl">Help keep local information useful</h2>
          <p className="mt-3 max-w-2xl leading-7 text-white/75">Running an activity, managing a local service or spotted a useful place? Send it for review so more residents can find it.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
          <Link to="/directory#add-directory-listing" data-cta="home-add-directory-listing" onClick={() => trackCta("home-add-directory-listing", "/directory#add-directory-listing")} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-slate-950 hover:bg-slate-100">Add a directory listing <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          <Link to="/events#submit-event" data-cta="home-submit-event" onClick={() => trackCta("home-submit-event", "/events#submit-event")} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-slate-950 hover:bg-slate-100">Submit an event <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          <Link to="/map#add-location" data-cta="home-add-location" onClick={() => trackCta("home-add-location", "/map#add-location")} className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/25 bg-black/20 px-5 py-3 font-bold text-white hover:bg-black/30">Add a map location</Link>
        </div>
      </section>

      <WhatsAppCommunityCard
        title="Stay connected with Urban Unity Network"
        description="Join the community WhatsApp group for local events, services, consultations, opportunities and ways to get involved."
        showQrCode={false}
        compact
        source="home"
      />
    </div>
  );
}
